import React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Container,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { AccountCircle, Menu as MenuIcon } from '@mui/icons-material';
import UnrealVideoLogo from '../assets/UnrealVideoLogo.png';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isMobile = useMediaQuery('(max-width:855px)');
  const [localAuth, setLocalAuth] = React.useState(isAuthenticated);

  React.useEffect(() => {
    setLocalAuth(isAuthenticated);
  }, [isAuthenticated]);

  // Add a useEffect hook to log authentication state changes
  React.useEffect(() => {
    console.log('Authentication state changed:', { isAuthenticated, localAuth });
  }, [isAuthenticated, localAuth]);

  const handleLogout = async () => {
    handleClose();
    // Update local auth state before calling logout
    setLocalAuth(false);
    console.log('Local auth state updated to false');
    // Call logout after updating local state
    await logout();
    console.log('Logout function completed');
    // Force a re-render by updating a state variable
    setMobileOpen(false);
    // Navigate to home page
    navigate('/', { replace: true });
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <List>
        <ListItem component={RouterLink} to="/pricing" onClick={handleDrawerToggle}>
          <ListItemText primary="Pricing" sx={{ color: 'primary.main' }} />
        </ListItem>
        {localAuth ? (
          <>
            <ListItem>
              <ListItemText primary={`Tokens: ${user?.tokenBalance?.toLocaleString() || '0'}`} sx={{ color: 'primary.main' }} />
            </ListItem>
            <Divider />
            {location.pathname !== '/scene-detection' && (
              <ListItem 
                component={RouterLink} 
                to="/scene-detection" 
                onClick={handleDrawerToggle}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  }
                }}
              >
                <ListItemText primary="Go to App" sx={{ color: 'inherit' }} />
              </ListItem>
            )}
            <ListItem component={RouterLink} to="/profile" onClick={handleDrawerToggle}>
              <ListItemText primary="Profile" sx={{ color: 'primary.main' }} />
            </ListItem>
            <ListItem component={RouterLink} to="/settings" onClick={handleDrawerToggle}>
              <ListItemText primary="Settings" sx={{ color: 'primary.main' }} />
            </ListItem>
            <Divider />
            <ListItem onClick={() => { handleDrawerToggle(); handleLogout(); }}>
              <ListItemText primary="Sign Out" sx={{ color: 'primary.main' }} />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem component={RouterLink} to="/login" onClick={handleDrawerToggle}>
              <ListItemText primary="Sign In" sx={{ color: 'primary.main' }} />
            </ListItem>
            <ListItem component={RouterLink} to="/register" onClick={handleDrawerToggle}>
              <ListItemText primary="Sign Up" sx={{ color: 'primary.main' }} />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="static" color="default" elevation={1} key={localAuth ? 'authenticated' : 'unauthenticated'}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Box
            component="a"
            href="https://unrealvideo.ai"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              minWidth: '220px',
              overflow: 'visible',
              marginRight: '16px',
            }}
          >
            <img 
              src={UnrealVideoLogo}
              alt="UnrealVideo.ai"
              style={{
                height: '40px',
                width: 'auto',
                objectFit: 'contain',
                padding: '4px 0',
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} key={localAuth ? 'authenticated' : 'unauthenticated'}>
            {!isMobile && (
              <>
                {localAuth ? (
                  <>
                    <Button
                      component={RouterLink}
                      to="/pricing"
                      color="inherit"
                      sx={{ textTransform: 'none', fontSize: '1rem' }}
                    >
                      Pricing
                    </Button>
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Tokens: {user?.tokenBalance?.toLocaleString() || '0'}
                    </Typography>
                    <IconButton
                      onClick={handleMenu}
                      color="inherit"
                    >
                      <AccountCircle />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      onClick={handleClose}
                    >
                      <MenuItem component={RouterLink} to="/profile">
                        Profile
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/settings">
                        Settings
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        Sign Out
                      </MenuItem>
                    </Menu>
                    {location.pathname !== '/scene-detection' && (
                      <Button
                        component={RouterLink}
                        to="/scene-detection"
                        variant="contained"
                        sx={{ 
                          textTransform: 'none', 
                          fontSize: '1rem',
                          borderRadius: 2,
                          px: 3,
                          py: 1,
                          background: 'linear-gradient(45deg, #6E3AE4 30%, #9B6FF6 90%)',
                          boxShadow: '0 3px 10px rgba(110, 58, 228, 0.3)',
                          border: 'none',
                          color: 'white',
                          fontWeight: 500,
                          '&:hover': {
                            background: 'linear-gradient(45deg, #4C28A8 30%, #6E3AE4 90%)',
                            boxShadow: '0 5px 15px rgba(110, 58, 228, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        Go to App
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      component={RouterLink}
                      to="/pricing"
                      color="inherit"
                      sx={{ textTransform: 'none', fontSize: '1rem' }}
                    >
                      Pricing
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/login"
                      color="inherit"
                      sx={{ textTransform: 'none', fontSize: '1rem' }}
                    >
                      Sign In
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/register"
                      variant="contained"
                      sx={{ 
                        textTransform: 'none', 
                        fontSize: '1rem',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(45deg, #6E3AE4 30%, #9B6FF6 90%)',
                        boxShadow: '0 3px 10px rgba(110, 58, 228, 0.3)',
                        border: 'none',
                        color: 'white',
                        fontWeight: 500,
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4C28A8 30%, #6E3AE4 90%)',
                          boxShadow: '0 5px 15px rgba(110, 58, 228, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      Sign Up
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/scene-detection"
                      variant="contained"
                      sx={{ 
                        textTransform: 'none', 
                        fontSize: '1rem',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(45deg, #6E3AE4 30%, #9B6FF6 90%)',
                        boxShadow: '0 3px 10px rgba(110, 58, 228, 0.3)',
                        border: 'none',
                        color: 'white',
                        fontWeight: 500,
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4C28A8 30%, #6E3AE4 90%)',
                          boxShadow: '0 5px 15px rgba(110, 58, 228, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      Go to App
                    </Button>
                  </>
                )}
              </>
            )}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: 'block' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar; 